import React, { useEffect, useState } from "react";
import { Box, Typography } from "../../../design-system";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * FlagshipPhoneToTimeline — the "In Premiere" mock for the plugin hero.
 *
 * A small phone on the LEFT (styled like the real mobile ET) auto-ticks clips one by one; as each
 * ticks, a red clip (with the CherryClipz logo) pops onto a Premiere-style TIMELINE on the right
 * (V1 video + A1 audio), in about a second — then it loops. Deterministic mock (no iframe) so the
 * story reads clearly: pick on your phone → it lands on your Premiere timeline, live. Self-contained
 * dark chrome so it reads in either page theme.
 */

const font = "Outfit, sans-serif";
const mono = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";

const CLIPS = [
  { spk: "A", emo: "−", txt: "When you've been brought up with the earth…", g: ["#5a3b2a", "#2a1c14"], t: "00:06→00:16", w: 26 },
  { spk: "D", emo: "−", txt: "Once you're born in the projects…", g: ["#3a2f4a", "#1c1626"], t: "00:18→00:20", w: 18 },
  { spk: "E", emo: "=", txt: "What is God trying to tell us?", g: ["#6a1e2a", "#2c0f15"], t: "00:23→00:25", w: 22 },
  { spk: "F", emo: "+", txt: "What is this wondrous, mysterious sound?", g: ["#26343f", "#12191f"], t: "00:26→00:28", w: 20 },
  { spk: "E", emo: "+", txt: "Music is the pulse of the community, the culture.", g: ["#2a3a2c", "#141c16"], t: "00:30→00:34", w: 24 },
  { spk: "A", emo: "=", txt: "You have to take a leap of faith and do what feels right.", g: ["#3a2a1e", "#1c140e"], t: "00:38→00:44", w: 28 },
];
const N = CLIPS.length;
const EMO_COLOR: Record<string, string> = { "+": "#4ade80", "=": "#cbd5e1", "−": "#f87171" };

const FlagshipPhoneToTimeline: React.FC = () => {
  // step 0..N-1 fills clips; N..N+2 holds the full cut; then wraps to 0 (clean reset).
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setStep((s) => (s + 1) % (N + 3)), 1300);
    return () => window.clearInterval(id);
  }, []);
  const ticked = Math.min(step, N);

  return (
    <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: { xs: "16px", md: "20px" }, flexWrap: { xs: "wrap", md: "nowrap" } }}>
      {/* ── PHONE (left), in a labeled column ── */}
      <Box sx={{ flex: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
      <Box sx={{ position: "relative", boxSizing: "content-box", width: 300, height: 624, borderRadius: "48px", border: "14px solid #15131b", background: "#0b0a0f", overflow: "hidden", boxShadow: "0 44px 96px -28px rgba(0,0,0,0.85)" }}>
        <Box aria-hidden sx={{ position: "absolute", top: 9, left: "50%", transform: "translateX(-50%)", width: 84, height: 19, borderRadius: "999px", background: "#000", border: "1px solid rgba(255,255,255,0.06)", zIndex: 2 }} />
        <Box sx={{ p: "16px 9px 12px", height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
          {/* header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "6px", px: "2px" }}>
            <img src={logoSite} alt="" height={12} style={{ width: "auto" }} />
            <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "10px", color: "#f3f4f9" }}>CherryClipz</Typography>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ width: 4.5, height: 4.5, borderRadius: "50%", background: "#e61d50" }} />
            <Typography sx={{ fontFamily: mono, fontSize: "7px", color: "#ff9db4" }}>LIVE</Typography>
          </Box>
          {/* stats strip */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "3px", py: "4px", mb: "6px", borderTop: "1px solid rgba(255,255,255,0.07)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <Box>
              <Typography sx={{ fontFamily: mono, fontSize: "6.5px", color: "#6c727d", letterSpacing: ".06em" }}>CLIPS</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: "#f3f4f9", lineHeight: 1 }}>{N}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontFamily: mono, fontSize: "6.5px", color: "#6c727d", letterSpacing: ".06em" }}>SELECTED</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: "#ff9db4", lineHeight: 1 }}>{ticked}</Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ fontFamily: font, fontSize: "7px", fontWeight: 700, color: "#c2c7d6", px: "6px", py: "3px", borderRadius: "5px", border: "1px solid rgba(255,255,255,0.14)" }}>Filter ▸</Box>
          </Box>
          {/* rows */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {CLIPS.map((c, i) => {
              const on = i < ticked;
              return (
                <Box key={i} sx={{ display: "flex", gap: "5px", p: "3px", borderRadius: "5px", background: on ? "rgba(230,29,80,0.15)" : "transparent", borderLeft: `2px solid ${on ? "#e61d50" : "transparent"}`, transition: "background .3s ease" }}>
                  <Box sx={{ mt: "2px", width: 15, height: 15, borderRadius: "4px", flex: "none", border: `1.5px solid ${on ? "#e61d50" : "rgba(255,255,255,0.28)"}`, background: on ? "#e61d50" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .25s ease" }}>
                    {on && <Box component="span" sx={{ color: "#fff", fontSize: "10px", fontWeight: 900, lineHeight: 1 }}>✓</Box>}
                  </Box>
                  {/* thumbnail + speaker chip */}
                  <Box sx={{ flex: "none", width: 58 }}>
                    <Box sx={{ position: "relative", width: 58, height: 33, borderRadius: "4px", overflow: "hidden", background: `linear-gradient(150deg, ${c.g[0]}, ${c.g[1]})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <img src={logoSite} alt="" height={13} style={{ width: "auto", opacity: 0.85 }} />
                      <Box sx={{ position: "absolute", left: 2, bottom: 2, display: "flex", alignItems: "center", gap: "2px", px: "2px", borderRadius: "3px", background: "rgba(8,7,11,0.7)" }}>
                        <Box component="span" sx={{ fontFamily: font, fontSize: "6.5px", fontWeight: 800, color: EMO_COLOR[c.emo] }}>{c.emo}</Box>
                        <Box component="span" sx={{ fontFamily: font, fontSize: "6.5px", fontWeight: 800, color: "#e8eaf3" }}>{c.spk}</Box>
                      </Box>
                    </Box>
                    <Typography sx={{ fontFamily: mono, fontSize: "6px", color: "#8a8f9c", mt: "2px", textAlign: "center", lineHeight: 1.1 }}>{c.t}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: font, fontSize: "9.5px", color: on ? "#f3f4f9" : "#aab0be", lineHeight: 1.3, minWidth: 0, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{c.txt}</Typography>
                </Box>
              );
            })}
          </Box>
          {/* select-clips pill */}
          <Box sx={{ mt: "auto", textAlign: "center", py: "8px", borderRadius: "999px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}>
            <Typography sx={{ fontFamily: font, fontSize: "8.5px", fontWeight: 700, color: "#c2c7d6" }}>Tap a clip → it cuts in Premiere</Typography>
          </Box>
        </Box>
      </Box>
        <Typography sx={{ fontFamily: font, fontSize: "15px", fontWeight: 700, color: "#c2c7d6", textAlign: "center" }}>① Your phone — tick a clip</Typography>
      </Box>

      {/* arrow */}
      <Box aria-hidden sx={{ flex: "none", color: "#e61d50", fontSize: "42px", fontWeight: 300, transform: { xs: "rotate(90deg)", md: "none" } }}>→</Box>

      {/* ── PREMIERE TIMELINE (right), in a labeled column ── */}
      <Box sx={{ flex: 1, minWidth: { xs: "280px", md: 0 }, maxWidth: 920, display: "flex", flexDirection: "column", gap: "12px" }}>
      <Box sx={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #000", background: "#1c1c1e", boxShadow: "0 30px 70px -40px rgba(0,0,0,0.9)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", px: "12px", py: "7px", background: "#2b2b2e", borderBottom: "1px solid #000" }}>
          <Box sx={{ display: "flex", gap: "5px" }}>
            {["#ff5f57", "#febc2e", "#28c840"].map((c) => <Box key={c} sx={{ width: 9, height: 9, borderRadius: "50%", background: c }} />)}
          </Box>
          <Typography sx={{ fontFamily: mono, fontSize: "10px", color: "#b9b9bf" }}>Premiere Pro — CherryClipz</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontFamily: mono, fontSize: "9.5px", color: "#8a8a90" }}>{ticked} clip{ticked === 1 ? "" : "s"} · Sequence 02</Typography>
        </Box>
        {/* program monitor strip */}
        <Box sx={{ height: 112, background: "radial-gradient(120% 100% at 50% 30%, #241019, #08060a)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontFamily: mono, fontSize: "9.5px", color: "#6a6a70" }}>{ticked ? "▶ your cut assembles" : "program monitor"}</Typography>
        </Box>
        {/* ruler */}
        <Box sx={{ display: "flex", justifyContent: "space-between", pl: "34px", pr: "10px", py: "4px", background: "#232326", borderTop: "1px solid #000" }}>
          {["00:00", "00:10", "00:20", "00:30"].map((t) => <Typography key={t} sx={{ fontFamily: mono, fontSize: "8px", color: "#6a6a70" }}>{t}</Typography>)}
        </Box>
        {/* tracks */}
        <Box sx={{ background: "#232326", px: "10px", pb: "10px", position: "relative" }}>
          {(["V1", "A1"] as const).map((track) => (
            <Box key={track} sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "5px" }}>
              <Box sx={{ width: 26, flex: "none", textAlign: "center" }}>
                <Typography sx={{ fontFamily: mono, fontSize: "9px", color: "#8a8a90" }}>{track}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, height: 40, borderRadius: "5px", background: "rgba(0,0,0,0.35)", display: "flex", gap: "4px", p: "4px", overflow: "hidden" }}>
                {CLIPS.slice(0, ticked).map((c, i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: `0 0 ${c.w * 3.8}px`, borderRadius: "4px", overflow: "hidden", position: "relative",
                      border: "1px solid #ec2b57", display: "flex", alignItems: "center", justifyContent: "center",
                      background: track === "V1"
                        ? "linear-gradient(180deg,#5a1e30,#3a1420)"
                        : "repeating-linear-gradient(90deg,#5a1e30 0 2px,#8a3050 2px 4px)",
                      boxShadow: i === ticked - 1 ? "0 0 0 1px #ec2b57, 0 0 12px rgba(236,43,87,0.55)" : "none",
                      animation: i === ticked - 1 ? "ccpPop .32s ease" : "none",
                      "@keyframes ccpPop": { "0%": { transform: "translateY(-7px) scaleX(0.6)", opacity: 0 }, "100%": { transform: "none", opacity: 1 } },
                    }}
                  >
                    {track === "V1" && <img src={logoSite} alt="" height={16} style={{ width: "auto", opacity: 0.92 }} />}
                    {track === "V1" && <Box sx={{ position: "absolute", top: 2, left: 3 }}><Typography sx={{ fontFamily: mono, fontSize: "7.5px", fontWeight: 700, color: "#ffb9c8" }}>{i + 1}</Typography></Box>}
                  </Box>
                ))}
                {ticked === 0 && <Box sx={{ alignSelf: "center", ml: "6px" }}><Typography sx={{ fontFamily: mono, fontSize: "8.5px", color: "#5a5a60" }}>empty — tick a clip on your phone</Typography></Box>}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
        <Typography sx={{ fontFamily: font, fontSize: "15px", fontWeight: 700, color: "#c2c7d6", textAlign: "center" }}>② Premiere Pro timeline — your clips land here</Typography>
      </Box>
    </Box>
  );
};

export default FlagshipPhoneToTimeline;
